import { createTheme } from '@mui/material/styles';

// Neutral ramp. The previous theme had none, which is why 166 literal hex values
// accumulated in App.js. Warm cast so the page ground is not clinical white.
const grey = {
  50: '#FAFAF7',  // page ground
  100: '#F2F1EC', // alternate section surface
  200: '#E4E3DD', // hairlines, card borders
  300: '#CFCEC6', // stronger dividers
  400: '#A8A79E', // disabled text
  500: '#7C7B73',
  600: '#5C6875', // text.secondary, 5.7:1 on white
  700: '#44505E',
  800: '#2A3644',
  900: '#14202E', // text.primary, 16.5:1 on white
};

const theme = createTheme({
  palette: {
    // One accent with one job: the booking action. Nothing else may use it.
    secondary: {
      main: '#C87941',
      light: '#D99563', // safe as text on the navy hero
      dark: '#8A4E22',  // 6.6:1 on white, safe as text on light
      contrastText: '#FFFFFF',
    },
    primary: {
      main: '#1E3A5F', // 11.5:1 on white. Structure, headings, dark grounds.
      light: '#2D5A8F',
      dark: '#0F1F3D',
      contrastText: '#FFFFFF',
    },
    // Semantic colours stay semantic: .main for fills and icons, .dark is the
    // only step allowed as text below 24px.
    success: { main: '#10B981', dark: '#047857', contrastText: '#FFFFFF' },
    warning: { main: '#F59E0B', dark: '#B45309', contrastText: '#FFFFFF' },
    error: { main: '#EF4444', dark: '#B91C1C', contrastText: '#FFFFFF' },
    info: { main: '#2A9D8F', dark: '#1F7A6F' },
    grey,
    divider: grey[200],
    background: {
      default: grey[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: grey[900],
      secondary: grey[600],
      disabled: grey[400],
    },
  },

  // Six distinct steps, no collisions. The old scale had h6 and body1 both at
  // 1.125rem, which is why fontWeight was overridden 101 times to fake hierarchy.
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: { fontSize: 'clamp(2.5rem, 4.5vw + 1rem, 3.5rem)', fontWeight: 800, lineHeight: 1.07, letterSpacing: '-0.022em' },
    h2: { fontSize: 'clamp(1.875rem, 2.6vw + 0.9rem, 2.75rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.018em' },
    h3: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.286, letterSpacing: '-0.014em' },
    h4: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.273, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.556 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.005em' },
    subtitle1: { fontSize: '1.25rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '-0.005em' },
    body1: { fontSize: '1.0625rem', fontWeight: 400, lineHeight: 1.647 },
    body2: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.6 },
    caption: { fontSize: '0.8125rem', lineHeight: 1.538 },
    // The one eyebrow style. Replaces every Chip used as a section label.
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.333,
      letterSpacing: '0.09em',
      textTransform: 'uppercase',
    },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.005em' },
  },

  shape: { borderRadius: 8 },
  spacing: 8,

  // Two layer, negative spread, alpha at or below 0.18, neutral rather than
  // navy tinted. Levels 4 and 5 are for overlays only.
  shadows: (() => {
    const s = [
      'none',
      '0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)',
      '0 4px 6px -1px rgb(0 0 0 / 0.08), 0 2px 4px -2px rgb(0 0 0 / 0.08)',
      '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.08)',
      '0 20px 25px -5px rgb(0 0 0 / 0.10), 0 8px 10px -6px rgb(0 0 0 / 0.10)',
      '0 25px 50px -12px rgb(0 0 0 / 0.18)',
    ];
    return [...s, ...Array(19).fill(s[5])];
  })(),

  transitions: {
    duration: { shortest: 100, shorter: 150, short: 200, standard: 250, complex: 300 },
    easing: {
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Nothing may ever spill sideways. Without this a single over-wide
        // child pushes the whole document and everything reads as cut off at
        // the right edge, which is very hard to trace back to its source.
        html: {
          WebkitFontSmoothing: 'antialiased',
          scrollBehavior: 'smooth',
          maxWidth: '100%',
          overflowX: 'hidden',
        },
        body: { backgroundColor: grey[50], maxWidth: '100%', overflowX: 'hidden' },
        img: { maxWidth: '100%', display: 'block' },
        // The page previously had zero focus-visible handling anywhere.
        ':focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        '.prose': { maxWidth: '68ch' },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 28px',
          fontSize: '1rem',
          transition: 'background-color 150ms cubic-bezier(0,0,0.2,1), border-color 150ms cubic-bezier(0,0,0.2,1)',
        },
        containedSecondary: {
          backgroundColor: '#C87941',
          '&:hover': { backgroundColor: '#B26A38' },
          '&:active': { backgroundColor: '#8A4E22' },
        },
        sizeLarge: { padding: '16px 40px', fontSize: '1.125rem' },
      },
    },

    // Static cards do not float. Shadow means the thing moves or overlays.
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: 10, border: `1px solid ${grey[200]}`, backgroundImage: 'none' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          transition: 'transform 150ms cubic-bezier(0,0,0.2,1), box-shadow 150ms cubic-bezier(0,0,0.2,1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.08)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: { root: { borderRadius: 6, fontWeight: 600, fontSize: '0.8125rem' } },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: grey[200] } } },
    MuiAccordion: {
      defaultProps: { elevation: 0, disableGutters: true },
      styleOverrides: {
        root: {
          border: `1px solid ${grey[200]}`,
          '&::before': { display: 'none' },
          '&:not(:last-of-type)': { borderBottom: 0 },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 20,
          paddingRight: 20,
          '@media (min-width:900px)': { paddingLeft: 40, paddingRight: 40 },
        },
      },
    },
  },
});

export default theme;
